import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import InstructorTrustStack from "../components/instructor/InstructorTrustStack";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import axios from "axios";
import {
  Car,
  CheckCircle,
  ChevronRight,
  KeyRound,
  User,
  MapPin,
  Phone,
  Brain,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { formatApiError } from "../utils/apiError";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = { PIN: 0, DETAILS: 1, QUESTIONNAIRE: 2, SUCCESS: 3 };

export default function PupilInvite() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.PIN);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null);

  // PIN
  const [pin, setPin] = useState("");

  // Details
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");

  // Questionnaire
  const [hasDriven, setHasDriven] = useState(null);
  const [experience, setExperience] = useState("");
  const [confidence, setConfidence] = useState("");
  const [concerns, setConcerns] = useState([]);

  useEffect(() => {
    fetchInvite();
  }, [code]);

  const fetchInvite = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/work-mode/invite/${code}`);
      setInviteInfo(data);
      if (data.already_completed) {
        setStep(STEPS.SUCCESS);
      }
    } catch {
      setInviteInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPin = async () => {
    if (pin.length !== 4) return;
    setActionLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API_URL}/api/work-mode/invite/${code}/verify`,
        { pin }
      );
      setFullName(data.student_name || "");
      setPhone(data.phone || "");
      setStep(STEPS.DETAILS);
    } catch (err) {
      setError(formatApiError(err, "Incorrect PIN. Please try again."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDetailsNext = () => {
    if (!fullName.trim() || !phone.trim() || !address.trim() || !postcode.trim()) {
      setError("Please fill in all fields");
      return;
    }
    setError("");
    setStep(STEPS.QUESTIONNAIRE);
  };

  const handleSubmit = async () => {
    if (hasDriven === null || !experience || !confidence) {
      setError("Please answer all questions");
      return;
    }
    setActionLoading(true);
    setError("");
    try {
      await axios.post(`${API_URL}/api/work-mode/invite/${code}/onboard`, {
        full_name: fullName.trim(),
        phone: phone.trim(),
        address: address.trim(),
        postcode: postcode.trim().toUpperCase(),
        has_driven_before: hasDriven,
        experience_level: experience,
        confidence_level: confidence,
        concerns,
      });
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setError(formatApiError(err, "Something went wrong"));
    } finally {
      setActionLoading(false);
    }
  };

  const toggleConcern = (c) => {
    setConcerns((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    );
  };

  // Progress bar
  const progress = step === STEPS.PIN ? 0 : step === STEPS.DETAILS ? 33 : step === STEPS.QUESTIONNAIRE ? 66 : 100;

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!inviteInfo || !inviteInfo.valid) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Invalid Link</h1>
          <p className="text-slate-500 mb-6">
            This invite link is not valid. Please ask your instructor for a new one.
          </p>
          <Button onClick={() => navigate("/")} variant="outline" className="rounded-xl">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Car className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-bold text-slate-900">National Driving Guild</h1>
              <p className="text-xs text-slate-500">
                Invited by {inviteInfo.instructor_name}
              </p>
            </div>
          </div>
          {inviteInfo.instructor_trust && (
            <div className="mt-3" data-testid="invite-instructor-trust">
              <InstructorTrustStack instructorId={inviteInfo.instructor_id} trust={inviteInfo.instructor_trust} max={5} />
            </div>
          )}
          {/* Progress */}
          {step < STEPS.SUCCESS && (
            <div className="mt-4 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 py-8">
        {/* Step 1: PIN */}
        {step === STEPS.PIN && (
          <div data-testid="step-pin" className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">
                Welcome, {inviteInfo.student_name}
              </h2>
              <p className="text-slate-500">
                Enter the 4-digit PIN your instructor gave you
              </p>
            </div>

            <div className="flex justify-center">
              <input
                data-testid="pin-input"
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="0000"
                className="w-48 text-center text-4xl font-mono font-black tracking-[0.5em] bg-white border-2 border-slate-200 rounded-2xl py-4 focus:outline-none focus:border-blue-500"
                autoFocus
              />
            </div>

            {error && (
              <p data-testid="pin-error" className="text-red-500 text-sm text-center">{error}</p>
            )}

            <Button
              data-testid="verify-pin-btn"
              onClick={handleVerifyPin}
              disabled={pin.length !== 4 || actionLoading}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl text-lg"
            >
              {actionLoading ? (
                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  Continue
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Personal Details */}
        {step === STEPS.DETAILS && (
          <div data-testid="step-details" className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">
                Your Details
              </h2>
              <p className="text-slate-500">
                This helps your instructor plan your lessons
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-slate-700 font-medium">Full Name</Label>
                <Input
                  data-testid="name-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  className="h-12 rounded-xl mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Phone Number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="07700 900 123"
                    className="h-12 rounded-xl pl-10"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Home Address</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <textarea
                    data-testid="address-input"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Your full address"
                    rows={2}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Postcode</Label>
                <Input
                  data-testid="postcode-input"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  placeholder="B1 2AA"
                  className="h-12 rounded-xl mt-1 uppercase font-mono tracking-wider"
                  maxLength={8}
                />
              </div>
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            <Button
              data-testid="details-next-btn"
              onClick={handleDetailsNext}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl text-lg"
            >
              Continue
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 3: Driving Questionnaire */}
        {step === STEPS.QUESTIONNAIRE && (
          <div data-testid="step-questionnaire" className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Brain className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">
                About Your Driving
              </h2>
              <p className="text-slate-500">
                A few quick questions to help plan your lessons
              </p>
            </div>

            {/* Have you driven before? */}
            <div>
              <p className="font-semibold text-slate-900 text-sm mb-3">
                Have you driven before?
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ].map((opt) => (
                  <button
                    key={String(opt.value)}
                    data-testid={`driven-${opt.value}`}
                    onClick={() => setHasDriven(opt.value)}
                    className={`p-4 rounded-xl border-2 font-semibold text-sm transition-all ${
                      hasDriven === opt.value
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Experience Level */}
            <div>
              <p className="font-semibold text-slate-900 text-sm mb-3">
                Experience level
              </p>
              <div className="space-y-2">
                {[
                  { value: "beginner", label: "Complete beginner", desc: "Never driven on the road" },
                  { value: "some_experience", label: "Some experience", desc: "Had a few lessons" },
                  { value: "nearly_test_ready", label: "Nearly test ready", desc: "Close to taking my test" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`exp-${opt.value}`}
                    onClick={() => setExperience(opt.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      experience === opt.value
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <p className={`font-semibold text-sm ${experience === opt.value ? "text-blue-700" : "text-slate-700"}`}>
                      {opt.label}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Confidence */}
            <div>
              <p className="font-semibold text-slate-900 text-sm mb-3">
                How confident do you feel about driving?
              </p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: "low", label: "Low" },
                  { value: "medium", label: "Medium" },
                  { value: "high", label: "High" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`conf-${opt.value}`}
                    onClick={() => setConfidence(opt.value)}
                    className={`p-4 rounded-xl border-2 font-semibold text-sm transition-all ${
                      confidence === opt.value
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Concerns */}
            <div>
              <p className="font-semibold text-slate-900 text-sm mb-2">
                Any concerns? <span className="font-normal text-slate-400">(optional)</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "nervous", label: "Feeling nervous" },
                  { value: "overthinking", label: "Overthinking" },
                  { value: "struggle_with_focus", label: "Struggle with focus" },
                  { value: "other", label: "Other" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`concern-${opt.value}`}
                    onClick={() => toggleConcern(opt.value)}
                    className={`px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                      concerns.includes(opt.value)
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            <Button
              data-testid="submit-onboard-btn"
              onClick={handleSubmit}
              disabled={actionLoading}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl text-lg"
            >
              {actionLoading ? (
                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Complete Setup
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 4: Success */}
        {step === STEPS.SUCCESS && (
          <div data-testid="step-success" className="text-center space-y-6 pt-8">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">
                You're All Set!
              </h2>
              <p className="text-slate-500 leading-relaxed">
                Your instructor now has everything they need to plan your lessons.
                You'll get the most out of every session.
              </p>
            </div>

            {/* CTA branch (P0 fix · Feb 2026):
                 When `already_completed` is true on a re-visit, the invite has
                 already been redeemed by a registered user — pushing them to
                 /register would dead-end at "Email already registered". Promote
                 "Log in" as the primary CTA in that case and demote
                 "Create account" to a small secondary link.
                 On a fresh post-onboarding success, keep the original ordering
                 ("Create your account" primary). */}
            {inviteInfo?.already_completed ? (
              <div
                data-testid="success-cta-already-completed"
                className="bg-blue-50 border border-blue-200 rounded-2xl p-5"
              >
                <p className="text-blue-800 font-medium text-sm mb-2">
                  Already onboarded — pick up where you left off
                </p>
                <p className="text-blue-600 text-sm mb-4">
                  Log in to access your lessons, confidence checks, and progress.
                </p>
                <Button
                  data-testid="login-btn-primary"
                  onClick={() => navigate(`/login?invite_code=${code}`)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl"
                >
                  Log in
                </Button>
                <p className="text-xs text-blue-500 mt-3">
                  Don't have an account yet?{" "}
                  <button
                    onClick={() => navigate(`/register?invite_code=${code}`)}
                    className="font-semibold underline"
                    data-testid="create-account-link-secondary"
                  >
                    Create one
                  </button>
                </p>
              </div>
            ) : (
              <div
                data-testid="success-cta-fresh-onboard"
                className="bg-blue-50 border border-blue-200 rounded-2xl p-5"
              >
                <p className="text-blue-800 font-medium text-sm mb-2">
                  Want to track your own progress?
                </p>
                <p className="text-blue-600 text-sm mb-4">
                  Create a free account to access lessons, confidence checks, and more.
                </p>
                <Button
                  data-testid="create-account-btn"
                  onClick={() => navigate(`/register?invite_code=${code}`)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl"
                >
                  Create Your Account
                </Button>
                <p className="text-xs text-blue-500 mt-3">
                  Already have an account?{" "}
                  <button
                    onClick={() => navigate(`/login?invite_code=${code}`)}
                    className="font-semibold underline"
                    data-testid="login-link-invite"
                  >
                    Log in to link
                  </button>
                </p>
              </div>
            )}

            <p className="text-xs text-slate-400">
              You can close this page. Your instructor will take it from here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
